import { useEffect, useState, useCallback } from 'react'

import { useLazyQuery } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { Combobox, TextInput, TextInputProps, useCombobox } from '@mantine/core'
import { useDebouncedState, useInViewport } from '@mantine/hooks'

import { GET_CARRIER_BY_DOT_NUMBER } from 'src/graphql/carriers.graphql'
import { SEARCH_FMCSA_COMPANY_BY_DOT_NUMBER } from 'src/graphql/fmcsa.graphql'

import CarrierList from './CarrierList'
import ContactList from './ContactList'

export interface CarrierAutocompleteProps
  extends Omit<TextInputProps, 'onChange'> {
  onSelect?: (selectedItem) => void
  onContactSelect?: (contact) => void
  enableContacts?: boolean
  classNames?: Record<string, string>
  onChange?: (value: string) => void
  // Where this autocomplete is rendered (e.g. 'dashboard'), used to segment
  // fmcsa_* RUM events by surface
  surface?: string
  // Wider than the input when set, opening leftward from its right edge, for
  // narrow inputs whose results need room (DOT number, fleet size, HQ)
  dropdownWidth?: number
}

const DotAutocomplete = ({
  onSelect,
  onContactSelect,
  enableContacts = false,
  classNames,
  onChange,
  dropdownWidth,
  surface = 'unknown',
  ...props
}: CarrierAutocompleteProps) => {
  const combobox = useCombobox()
  const [inputDebounced, setInputDebounced] = useDebouncedState('', 400)
  const [results, setResults] = useState([])
  const [selectedCarrier, setSelectedCarrier] = useState(null)
  const [_selectedContact, setSelectedContact] = useState(null)
  const [showContacts, setShowContacts] = useState(false)
  const { ref, inViewport } = useInViewport()
  const MINIMUM_CHARACTERS = 2

  const [
    searchQuery,
    { loading: searching, data: searchResults, error: searchError },
  ] = useLazyQuery(SEARCH_FMCSA_COMPANY_BY_DOT_NUMBER, {
    onCompleted: (data) => {
      const company = data?.searchResult?.company
      if (company) {
        setResults([company])
      } else {
        setResults([])
      }
    },
  })

  const [
    getCarrierByDotNumber,
    { loading: loadingCarrier, data: carrierData, error: carrierError },
  ] = useLazyQuery(GET_CARRIER_BY_DOT_NUMBER)

  // Separate query just for DB presence tracking
  const [checkCarrierInDb] = useLazyQuery(GET_CARRIER_BY_DOT_NUMBER, {
    onCompleted: (data) => {
      // Track if selected carrier exists in our DB
      if (selectedCarrier) {
        datadogRum.addAction('fmcsa_carrier_db_presence', {
          component: 'DotAutocomplete',
          surface,
          selected_dot_number: selectedCarrier.dotNumber,
          selected_company_name: selectedCarrier.companyName,
          exists_in_db: Boolean(data?.carrierByDotNumber),
          has_contacts: Boolean(data?.carrierByDotNumber?.contacts?.length),
        })
      }
    },
  })

  const handleOptionSubmit = (option) => {
    if (showContacts) {
      // Handle contact selection
      setSelectedContact(option)
      // Update input to show contact name with carrier info
      const contactDisplayValue = `${
        option.contactName || 'Unnamed Contact'
      } (${selectedCarrier?.companyName || ''})`
      if (onChange) {
        onChange(contactDisplayValue)
      }
      if (onContactSelect) {
        onContactSelect(option)
      }
      combobox.closeDropdown()
    } else {
      // Handle carrier selection
      setSelectedCarrier(option)
      setSelectedContact(null)
      // Update the input value to show the selected carrier
      const carrierName = option.companyName || ''
      if (onChange) {
        onChange(carrierName)
      }

      // Track carrier selection
      datadogRum.addAction('fmcsa_carrier_selected', {
        component: 'DotAutocomplete',
        surface,
        search_term: inputDebounced,
        selected_dot_number: option.dotNumber,
        selected_company_name: option.companyName,
        search_type: 'dot_number',
        will_show_contacts: enableContacts && option.dotNumber,
      })

      // Always check if carrier exists in DB for tracking
      if (option.dotNumber) {
        checkCarrierInDb({ variables: { dotNumber: option.dotNumber } })
      }

      if (enableContacts && option.dotNumber) {
        getCarrierByDotNumber({ variables: { dotNumber: option.dotNumber } })
        setShowContacts(true)
        // Don't close dropdown when selecting carrier - keep it open to show contacts
      } else {
        // Close dropdown when contacts are disabled or no dotNumber
        combobox.closeDropdown()
      }
      if (onSelect) {
        onSelect(option)
      }
    }
  }

  const handleBackToCarriers = () => {
    setShowContacts(false)
    setSelectedContact(null)
  }

  // Sync inputDebounced with external value prop when component is controlled
  useEffect(() => {
    if (props.value !== undefined) {
      setInputDebounced(String(props.value))
    }
  }, [props.value, setInputDebounced])

  useEffect(() => {
    const trimmed = inputDebounced.trim()
    if (trimmed.length >= MINIMUM_CHARACTERS) {
      searchQuery({ variables: { dotNumber: inputDebounced } })
    }
    setResults([])
  }, [inputDebounced, searchQuery])

  const handleLoadMore = useCallback(() => {
    const { pageInfo } = searchResults?.searchResult || {}
    if (pageInfo && Number.isInteger(pageInfo.offset)) {
      searchQuery({
        variables: {
          dotNumber: inputDebounced,
          pageInfo: { offset: pageInfo.offset + 1, limit: pageInfo.limit },
        },
      })
    }
  }, [searchQuery, inputDebounced, searchResults?.searchResult])

  return (
    <Combobox
      onOptionSubmit={handleOptionSubmit}
      store={combobox}
      withinPortal={false}
      position={dropdownWidth ? 'bottom-end' : 'bottom-start'}
      width={dropdownWidth ?? 'target'}
      offset={5}
    >
      <Combobox.Target>
        <TextInput
          classNames={
            classNames || {
              root: 'flex-1',
              label: 'text-xs font-medium text-doubleNickel-gray-700',
              input: `cursor-pointer [&[data-disabled]]:bg-doubleNickel-white [&[data-disabled]]:opacity-100 [&[data-disabled]]:text-doubleNickel-gray-600`,
            }
          }
          onChange={(event) => {
            const newValue = event.currentTarget.value
            if (onChange) {
              onChange(newValue)
            }
            setInputDebounced(newValue)
            // Reset selections when user starts typing
            setSelectedCarrier(null)
            setSelectedContact(null)
            setShowContacts(false)
            combobox.resetSelectedOption()
            combobox.openDropdown()
          }}
          onClick={(event) => {
            combobox.openDropdown()
            event.stopPropagation()
          }}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          {...(props as Omit<TextInputProps, 'onChange'>)}
        />
      </Combobox.Target>

      <Combobox.Dropdown
        styles={{ dropdown: { maxWidth: '100vh' } }}
        translate="no"
      >
        <Combobox.Options mah={300} className="overflow-y-auto">
          {showContacts && enableContacts ? (
            <ContactList
              selectedCarrier={selectedCarrier}
              contacts={carrierData?.carrierByDotNumber?.contacts || []}
              loadingCarrier={loadingCarrier}
              carrierError={carrierError}
              onBackToCarriers={handleBackToCarriers}
            />
          ) : (
            <CarrierList
              results={results}
              inputDebounced={inputDebounced}
              searching={searching}
              searchError={searchError}
              minimumCharacters={MINIMUM_CHARACTERS}
              inViewport={inViewport}
              searchResults={searchResults}
              onLoadMore={handleLoadMore}
              viewportRef={ref}
            />
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export default DotAutocomplete
